// External Libraries
import React from "react";
import * as Icon from "react-feather";
import _ from "lodash";
import moment from "moment";

import queryString from "query-string";
import Board from "react-trello";
import { Trello, ListChecks } from "lucide-react";
import clsx from "clsx";

// Services
import TaskService from "../../services/TaskService";
import UserService from "../../services/UserService";
import ClientService from "../../services/ClientService";
import UtilityService from "../../services/UtilityService";

// Common Components
import WComponent from "../common/WComponent";
import TaskModal from "./task-modal/TaskModal";
import TaskFilterModal from "./task-filter-modal/TaskFilterModal";
import FilterChip from "./filter-chip/FilterChip";
import TaskStats from "./task-stats/TaskStats";
import BulkActionModal, { BULK_ACTIONS } from "./bulk-action-modal/BulkActionModal";
import TaskRow from "./task-row/TaskRow";
import { userFullName, initialsOf, pluralize, dueBucket, dueMeta, isSequential, isInactiveEngagement, toArray } from "./taskUtils";
import ViewList from "../workflow/ViewList/ViewList";
import UserBubbles from "../common/UserBubbles/UserBubbles";
import ConfirmationModal from "../common/modal/confirmation_modal/ConfirmationModal";
import SearchInput from "../common/search_input/SearchInput";
import Loader from "../common/loader/Loader";
import ScrollableContainer from "../common/scrollable_container/ScrollableContainer";
import Checkbox from "../common/checkbox/Checkbox";
import PermissionGate from "../../permissions/permissionGate";
import RestrictedAccess from "../common/restricted_access/RestrictedAccess";

// Constants
import WORKFLOW from "../../constants/Workflow";
import { DEADLINES, ORDER_QUERIES, STATUSES, STATUS_ORDER, SORT_OPTIONS, GROUP_BY_OPTIONS, DUE_PRESETS } from "../../constants/Tasks";
import { TASK_DEFAULT_VIEWS } from "../../constants/Views";

// Styling
import "./tasks.css";

const KANBAN_STYLES = {
	deadline: "deadline",
	status: "status"
};

const DENSITIES = {
	comfortable: "comfortable",
	compact: "compact"
};

const MENUS = {
	status: "status",
	users: "users",
	clients: "clients",
	engagements: "engagements",
	due: "due",
	group: "group",
	sort: "sort",
	pageSize: "pageSize"
};

const PAGE_SIZES = [25, 50, 100, 200];

// Shape of the facet counts the API returns for the current scope
const EMPTY_FACETS = { status: {}, due: {}, users: {}, clients: {}, groups: {}, engagements: [] };

// Lane header colouring, keyed by the lane title react-trello hands back to the header component
const LANE_TONES = {
	Pending: "pending",
	Ready: "ready",
	"In Progress": "in_progress",
	Done: "done",
	Overdue: "overdue",
	Today: "today",
	"This Week": "week",
	"Next Week": "next_week",
	"After Next Week": "later"
};

const DUE_GROUPS = {
	overdue: { label: "Overdue", tone: "overdue" },
	today: { label: "Due today", tone: "today" },
	thisWeek: { label: "Due this week", tone: "week" },
	nextWeek: { label: "Due next week", tone: "next_week" },
	afterNextWeek: { label: "After next week", tone: "later" },
	none: { label: "No due date", tone: "none" },
	done: { label: "Completed", tone: "done" }
};

const DUE_GROUP_ORDER = ["overdue", "today", "thisWeek", "nextWeek", "afterNextWeek", "none", "done"];

const { alert, toDateOnly } = UtilityService;

/* ---------- component ---------- */

class Tasks extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,

			tasks: [],
			taskId: "new",
			search: "",

			sortField: ORDER_QUERIES.deadline,
			sortOrder: "asc",

			showTaskModal: false,
			showFilterModal: false,
			showViews: true,
			showFilters: true,

			pageView: "list",
			kanbanStyle: KANBAN_STYLES.status,
			taskViewMode: "all",
			density: DENSITIES.comfortable,
			groupBy: "none",

			isViewChanged: false,
			selectedTaskIds: [],

			clientId: 0,
			engagementId: 0,
			assignedUsers: [],
			assignedStatuses: [],
			assignedClients: [],
			assignedClientGroups: [],
			assignedEngagements: [],
			dueDate: WORKFLOW.defaultDateFunction,
			shouldShowInactive: false,

			openMenu: null,

			// Option lists for the searchable filter dropdowns
			userOptions: [],
			clientOptions: [],
			clientGroupOptions: [],

			// Counts for the current scope ignoring status and due narrowing: stat tiles, option counts, engagement list
			facets: EMPTY_FACETS,

			// Server-side paging of the list
			total: 0,
			page: 1,
			pageSize: 50,

			bulkAction: null,
			confirm: null,

			// True while a list request is in flight, drives the thin progress bar on the toolbar
			fetching: false,

			isNotAllowedResource: false,
			currentUser: null,
			title: "Tasks"
		};

		this.fetchTasksDebounce = _.debounce(this.fetchTasks, 300, {
			leading: false,
			trailing: true
		});

		// Monotonic counter so a slow, superseded response never overwrites a newer one
		this.fetchSeq = 0;
		this.mountedAt = Date.now();

		// Per-key cache for derived data (option lists, tiles, groups) so a keystroke does not recompute them
		this._memo = {};

		// One stable handler object for every TaskRow, so rows only re-render when their own data changes
		this.rowHandlers = {
			open: task => this.onTaskSelect(task.id),
			toggle: task => this.onTaskSelected(task.id),
			openStatusMenu: task => this.openMenu(`status:${task.id}`),
			closeMenus: () => this.closeMenus(),
			setStatus: (task, status) => {
				if (status !== task.status) {
					this.onTaskStatusUpdate({ taskId: task.id, status });
				} else {
					this.closeMenus();
				}
			},
			done: task => this.onTaskStatusUpdate({ taskId: task.id, status: STATUSES.done.id }),
			del: task => this.askDeleteTask(task),
			formatDate: value => this.formatDateOnly({ value, format: "MMMM Do YYYY" })
		};
	}

	// Returns the cached value for `name` while every dependency is referentially unchanged
	memo(name, deps, compute) {
		let previous = this._memo[name];

		if (previous && previous.deps.length === deps.length && previous.deps.every((dep, index) => dep === deps[index])) {
			return previous.value;
		}

		let value = compute();
		this._memo[name] = { deps, value };

		return value;
	}

	// Option counts for the dropdowns, straight from the facets the API computed for the current scope
	getCounts() {
		let { facets } = this.state;

		return {
			status: facets.status || {},
			user: facets.users || {},
			client: facets.clients || {},
			group: facets.groups || {},
			due: facets.due || {}
		};
	}

	// Every task in the current scope regardless of status or due date
	getScopeTotal() {
		let status = this.state.facets.status || {};

		return STATUS_ORDER.reduce((sum, id) => sum + (status[id] || 0), 0);
	}

	componentDidMount() {
		this.resetComponent();
		this.loadFilterOptions();
	}

	componentDidUpdate(prevProps) {
		let newProps = this.props;
		let oldProps = prevProps;

		if (
			oldProps.engagementId !== newProps.engagementId ||
			oldProps.title !== newProps.title ||
			oldProps.showViews !== newProps.showViews ||
			oldProps.showFilters !== newProps.showFilters
		) {
			this.resetComponent();
		}
	}

	resetComponent = async ({ shouldShowInactive = this.state.shouldShowInactive } = {}) => {
		let { clientId, engagementId, title, showViews, showFilters } = this.props;

		let user = await UserService.getUserData();

		let isViewingTasksOnMyTasksPage = UtilityService.isNullish(engagementId) && UtilityService.isNullish(clientId);

		let assignedUsers = [];

		if (isViewingTasksOnMyTasksPage) {
			assignedUsers = [
				{
					user_id: user.userId,
					userName: user.firstName + " " + user.lastName
				}
			];
		}

		await this.update({
			title: title || "Tasks",
			clientId: clientId || 0,
			engagementId: engagementId || 0,

			// The default view should be the currently logged in users tasks
			assignedUsers: assignedUsers,
			assignedClientGroups: [],
			assignedClients: [],
			assignedEngagements: [],
			assignedStatuses: [],
			dueDate: WORKFLOW.defaultDateFunction,
			shouldShowInactive,
			groupBy: "none",
			selectedTaskIds: [],
			page: 1,
			openMenu: null,
			confirm: null,
			bulkAction: null,
			taskViewMode: "all",
			currentUser: user,

			showViews,
			showFilters
		});

		await this.update({
			loading: true
		});

		await this.fetchTasks();

		await this.update({
			loading: false
		});

		if (this.props.location && this.props.location.search) {
			let params = queryString.parse(this.props.location.search);

			if (params.taskId) {
				this.onTaskSelect(params.taskId);
			}
		}
	};

	loadFilterOptions = async () => {
		try {
			let [users, clientsResult, clientGroups] = await Promise.all([
				UserService.fetchUsers({ isDetailed: true }),
				ClientService.searchClients({ search: "" }),
				ClientService.fetchClientGroups({ search: "" })
			]);

			let userOptions = toArray(users)
				.map(user => ({ id: user.id, name: userFullName(user) }))
				.filter(user => user.name);

			let clientOptions = toArray(clientsResult).map(client => ({
				id: client.id,
				name: client.name,
				type: client.type,
				clientGroupId: client.client_group_id
			}));

			let clientGroupOptions = toArray(clientGroups).map(group => ({ id: group.id, name: group.name }));

			await this.update({ userOptions, clientOptions, clientGroupOptions });
		} catch (error) {
			console.log(error);
		}
	};

	/* ---------- data ---------- */

	// The query sent to the API. Status and due-date narrowing can be left out to get the scope the stat tiles count against.
	getQueryParams = ({ includeStatusAndDue = true, paged = false } = {}) => {
		let {
			search,
			sortField,
			sortOrder,
			clientId,
			engagementId,
			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			assignedEngagements,
			dueDate,
			taskViewMode,
			currentUser,
			shouldShowInactive,
			showViews,
			page,
			pageSize
		} = this.state;

		let effectiveAssignedUsers = assignedUsers;
		let assignedByOther = false;
		let assignedBy;

		if (taskViewMode === "assignedToMe" && currentUser) {
			effectiveAssignedUsers = [{ user_id: currentUser.userId, userName: currentUser.firstName + " " + currentUser.lastName }];
			assignedByOther = true;
		}

		// Tasks I gave to others, without the default "assigned to me" filter
		if (taskViewMode === "assignedByMe" && currentUser) {
			effectiveAssignedUsers = [];
			assignedBy = currentUser.userId;
		}

		return {
			searchTerm: search,
			sortOrder,
			sortField,

			clientId,
			engagementId,

			assignedUsers: effectiveAssignedUsers,
			assignedStatuses: includeStatusAndDue ? assignedStatuses : [],
			assignedClients,
			assignedClientGroups,
			assignedEngagements,
			dueDate: includeStatusAndDue ? dueDate : WORKFLOW.defaultDateFunction,
			assignedByOther,
      assignedBy,

			// Only the standalone page has the "Show inactive" checkbox, embedded lists always show everything
			shouldShowInactive: showViews ? shouldShowInactive : true,

			// One page at a time, the API caps unpaged requests at 10,000 rows which the browser cannot render
			limit: paged ? pageSize : undefined,
			offset: paged ? (page - 1) * pageSize : 0
		};
	};

	// Loads the current page and its total. Facets (tiles, option counts) only need refreshing when the scope changed.
	fetchTasks = async ({ refreshFacets = true } = {}) => {
		let seq = ++this.fetchSeq;

		await this.update({ fetching: true });

		let query = this.getQueryParams({ paged: true });

		let [tasks, total] = await Promise.all([TaskService.list(query), TaskService.count(query)]);

		// A newer request started while this one was in flight, let that one win
		if (seq !== this.fetchSeq) {
			return;
		}

		if (UtilityService.isNotAllowedResource({ response: tasks })) {
			await this.update({
				isNotAllowedResource: true,
				fetching: false
			});
			return;
		}

		if (!Array.isArray(tasks)) {
			tasks = [];
		}

		let selectedTaskIds = this.state.selectedTaskIds.filter(id => tasks.some(task => task.id === id));

		await this.update({
			tasks,
			total: typeof total === "number" ? total : tasks.length,
			selectedTaskIds,
			fetching: false
		});

		if (refreshFacets) {
			await this.fetchFacets({ seq });
		}
	};

	// Counts for the tiles and dropdowns: the current scope without status or due narrowing
	fetchFacets = async ({ seq = this.fetchSeq } = {}) => {
		try {
			let facets = await TaskService.facets(this.getQueryParams({ includeStatusAndDue: false }));

			if (seq !== this.fetchSeq || !facets) {
				return;
			}

			await this.update({ facets: { ...EMPTY_FACETS, ...facets } });
		} catch (error) {
			console.log(error);
		}
	};

	refreshStats = async () => {
		await this.fetchFacets();
	};

	setPage = async page => {
		let pageCount = this.getPageCount();
		let next = Math.min(Math.max(1, page), pageCount);

		if (next === this.state.page) {
			return;
		}

		await this.update({ page: next, selectedTaskIds: [], openMenu: null });
		await this.fetchTasks({ refreshFacets: false });
	};

	setPageSize = async option => {
		await this.update({ pageSize: option.id, page: 1, selectedTaskIds: [], openMenu: null });
		await this.fetchTasks({ refreshFacets: false });
	};

	getPageCount() {
		let { total, pageSize } = this.state;

		return Math.max(1, Math.ceil(total / pageSize));
	}

	onSearch = async event => {
		await this.update({
			search: event.target.value
		});

		this.fetchTasksDebounce();
	};

	// Every filter change marks the view as changed so the views panel offers to save it
	setFilter = async patch => {
		await this.update({
			page: 1,
			...patch,
			isViewChanged: true,
			selectedTaskIds: []
		});

		await this.fetchTasks();
	};

	/* ---------- menus ---------- */

	openMenu = name => {
		this.update({ openMenu: name });
	};

	closeMenus = () => {
		if (this.state.openMenu !== null) {
			this.update({ openMenu: null });
		}
	};

	/* ---------- filters ---------- */

	toggleStatus = option => {
		let { assignedStatuses } = this.state;

		let next = assignedStatuses.includes(option.id) ? assignedStatuses.filter(id => id !== option.id) : assignedStatuses.concat([option.id]);

		this.setFilter({ assignedStatuses: next });
	};

	selectAllStatuses = () => {
		this.setFilter({ assignedStatuses: STATUS_ORDER.slice() });
	};

	clearStatuses = () => {
		this.setFilter({ assignedStatuses: [] });
	};

	toggleUser = option => {
		let { assignedUsers } = this.state;

		let exists = assignedUsers.some(user => user.user_id === option.id);

		let next = exists ? assignedUsers.filter(user => user.user_id !== option.id) : assignedUsers.concat([{ user_id: option.id, userName: option.label }]);

		this.setFilter({ assignedUsers: next });
	};

	clearUsers = () => {
		this.setFilter({ assignedUsers: [] });
	};

	toggleClient = option => {
		let { assignedClients } = this.state;

		let exists = assignedClients.some(client => client.client_id === option.id);

		let next = exists
			? assignedClients.filter(client => client.client_id !== option.id)
			: assignedClients.concat([{ client_id: option.id, clientName: option.label, clientType: option.type }]);

		this.setFilter({ assignedClients: next });
	};

	toggleClientGroup = option => {
		let { assignedClientGroups } = this.state;

		let exists = assignedClientGroups.some(group => group.client_group_id === option.id);

		let next = exists
			? assignedClientGroups.filter(group => group.client_group_id !== option.id)
			: assignedClientGroups.concat([{ client_group_id: option.id, clientGroupName: option.label }]);

		this.setFilter({ assignedClientGroups: next });
	};

	clearClients = () => {
		this.setFilter({ assignedClients: [], assignedClientGroups: [] });
	};

	toggleEngagement = option => {
		let { assignedEngagements } = this.state;

		let exists = assignedEngagements.some(engagement => engagement.engagement_id === option.id);

		let next = exists
			? assignedEngagements.filter(engagement => engagement.engagement_id !== option.id)
			: assignedEngagements.concat([{ engagement_id: option.id, engagementName: option.label }]);

		this.setFilter({ assignedEngagements: next });
	};

	clearEngagements = () => {
		this.setFilter({ assignedEngagements: [] });
	};

	setDuePreset = async presetId => {
		await this.update({ openMenu: null });

		this.setFilter({ dueDate: { dateFunction: presetId, payload: {} } });
	};

	clearAllFilters = () => {
		this.setFilter({
			search: "",
			assignedStatuses: [],
			assignedUsers: [],
			assignedClients: [],
			assignedClientGroups: [],
			assignedEngagements: [],
			dueDate: WORKFLOW.defaultDateFunction
		});
	};

	onShowFilterModal = async () => {
		await this.update({
			showFilterModal: true,
			openMenu: null
		});
	};

	onTaskFilterModalClose = async () => {
		await this.update({ showFilterModal: false });
	};

	// Applied from the full filter modal (date range picker, legacy assigners)
	onFilterChange = async ({ filters }) => {
		let { clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, dueDate } = filters;

		await this.setFilter({
			clientId,
			engagementId,

			assignedUsers: assignedUsers || [],
			assignedStatuses: assignedStatuses || [],
			assignedClients: assignedClients || [],
			assignedClientGroups: assignedClientGroups || [],
			dueDate: dueDate || WORKFLOW.defaultDateFunction
		});
	};

	/* ---------- sorting, grouping, density ---------- */

	sortBy = async field => {
		let { sortOrder } = this.state;

		await this.update({
			sortField: field,
			sortOrder: sortOrder === "desc" ? "asc" : "desc",
			isViewChanged: true,
			page: 1
		});

		await this.fetchTasks();
	};

	setSortField = async option => {
		await this.update({ openMenu: null });

		if (option.id === this.state.sortField) {
			return;
		}

		await this.update({ sortField: option.id, sortOrder: "asc", isViewChanged: true, page: 1 });
		await this.fetchTasks();
	};

	flipSortOrder = async () => {
		await this.update({ sortOrder: this.state.sortOrder === "asc" ? "desc" : "asc", isViewChanged: true, openMenu: null, page: 1 });
		await this.fetchTasks();
	};

	setGroupBy = async option => {
		await this.update({ groupBy: option.id, openMenu: null, isViewChanged: true });
	};

	toggleDensity = () => {
		this.update({ density: this.state.density === DENSITIES.compact ? DENSITIES.comfortable : DENSITIES.compact });
	};

	/* ---------- selection and bulk actions ---------- */

	onSelectAll = event => {
		let { tasks } = this.state;

		this.update({
			selectedTaskIds: event.target.checked ? tasks.map(task => task.id) : []
		});
	};

	onTaskSelected = taskId => {
		let { selectedTaskIds } = this.state;

		this.update({
			selectedTaskIds: selectedTaskIds.includes(taskId) ? selectedTaskIds.filter(id => id !== taskId) : selectedTaskIds.concat([taskId])
		});
	};

	clearSelection = () => {
		this.update({ selectedTaskIds: [] });
	};

	askConfirm = ({ title, warning, onConfirm }) => {
		this.update({ confirm: { title, warning, onConfirm } });
	};

	onConfirmClose = async ({ confirmed }) => {
		let { confirm } = this.state;

		await this.update({ confirm: null });

		if (confirmed && confirm && confirm.onConfirm) {
			await confirm.onConfirm();
		}
	};

	// Applies one update call per selected task and reloads
	applyToSelected = async ({ buildPayload, successText }) => {
		let { selectedTaskIds } = this.state;

		await this.update({ loading: true });

		let failures = 0;

		for (let taskId of selectedTaskIds) {
			let result = await TaskService.update({ taskId, ...buildPayload(taskId) });

			if (!result) {
				failures++;
			}
		}

		if (failures > 0) {
			alert({ type: "error", text: `${failures} of ${selectedTaskIds.length} tasks could not be updated.` });
		} else {
			alert({ type: "success", text: successText });
		}

		await this.update({ selectedTaskIds: [] });
		await this.fetchTasks();
		await this.update({ loading: false });
	};

	bulkMarkDone = async () => {
		let count = this.state.selectedTaskIds.length;

		await this.applyToSelected({
			buildPayload: () => ({ status: STATUSES.done.id }),
			successText: `${pluralize(count, "task")} marked done.`
		});
	};

	openBulkAction = action => {
		this.update({ bulkAction: action });
	};

	onBulkActionClose = async ({ confirmed, payload }) => {
		let { bulkAction, selectedTaskIds } = this.state;

		await this.update({ bulkAction: null });

		if (!confirmed || !payload) {
			return;
		}

		let count = selectedTaskIds.length;

		if (bulkAction === BULK_ACTIONS.reassign) {
			await this.applyToSelected({
				buildPayload: () => ({ assignedUsers: payload.assignedUsers }),
				successText: `${pluralize(count, "task")} reassigned.`
			});
			return;
		}

		await this.applyToSelected({
			buildPayload: () => ({ deadline: payload.deadline }),
			successText: `Deadline updated on ${pluralize(count, "task")}.`
		});
	};

	bulkDelete = () => {
		let { selectedTaskIds } = this.state;
		let count = selectedTaskIds.length;

		this.askConfirm({
			title: `Delete ${count} ${count === 1 ? "Task" : "Tasks"}?`,
			warning: "Are you sure you want to delete these tasks?",
			onConfirm: async () => {
				await this.update({ loading: true });

				await TaskService.bulkUpdate({ taskIds: selectedTaskIds, action: "delete" });

				await this.update({ selectedTaskIds: [] });
				await this.fetchTasks();
				await this.update({ loading: false });
			}
		});
	};

	/* ---------- single task actions ---------- */

	onNewTask = async () => {
		await this.update({
			taskId: "new",
			showTaskModal: true,
			openMenu: null
		});
	};

	onTaskSelect = async taskId => {
		this.update({
			taskId: taskId,
			showTaskModal: true,
			openMenu: null
		});
	};

	onTaskClose = async () => {
		await this.update({
			loading: true,
			showTaskModal: false
		});

		await this.fetchTasks();

		await this.update({
			loading: false
		});
	};

	formatDateOnly = ({ value, format = "YYYY-MM-DD" }) => {
		const dateOnly = toDateOnly({ value });
		if (!dateOnly) {
			return "-";
		}

		return moment(dateOnly, "YYYY-MM-DD").format(format);
	};

	// Replaces one task with a new object so pure rows see the change (in-place mutation would not re-render them)
	rerenderTask = async ({ task }) => {
		let { tasks } = this.state;

		let newTasks = tasks.map(existing => {
			if (existing.id !== task.id) {
				return existing;
			}

			return {
				...existing,
				name: task.name || existing.name,
				description_preview: task.description_preview || existing.description_preview,
				status: task.status || existing.status,
				read: true
			};
		});

		await this.update({
			tasks: newTasks
		});
	};

	// Steps of a sequential engagement that are not the current one: future ones still waiting their turn, past ones already completed.
	isDimmed = task => ["pending", "done"].includes(task.status) && isSequential(task);

	onTaskStatusUpdate = async ({ taskId, status }) => {
		let previousTasks = this.state.tasks;

		// Optimistic: show the new status immediately, the save runs in the background
		await this.update({
			openMenu: null,
			tasks: previousTasks.map(task => (task.id === taskId ? { ...task, status, read: true } : task))
		});

		let updatedTask = null;

		try {
			updatedTask = await TaskService.update({ taskId, status });
		} catch (error) {
			console.log(error);
		}

		if (!updatedTask) {
			// Roll back to what the server still has
			await this.update({ tasks: previousTasks });
			alert({ type: "error", text: "Updated error ..." });
			return;
		}

		alert({ type: "success", text: "Updated Successfully!" });

		// Completing a task can wake the next one, refetch so it shows up in its new lane.
		if (status === "done") {
			await this.fetchTasks();
			return;
		}

		await this.rerenderTask({ task: updatedTask });
		await this.refreshStats();
	};

	onCardMoveAcrossLanes = (fromLaneId, toLaneId, cardId, index) => {
		this.onTaskStatusUpdate({ taskId: cardId, status: toLaneId });
	};

	onCardClick = (cardId, laneId) => {
		this.onTaskSelect(cardId);
	};

	async deleteTask(taskId, engagementId) {
		try {
			await TaskService.update({ taskId, engagementId, isDeleted: true });
			alert({ type: "success", text: "Updated Successfully!" });

			await this.update({ selectedTaskIds: this.state.selectedTaskIds.filter(id => id !== taskId) });
			await this.fetchTasks();
		} catch (error) {
			alert({ type: "error", text: "Task update Error!" });
			console.error(error);
		}
	}

	askDeleteTask = task => {
		this.askConfirm({
			title: "Delete Task?",
			warning: "Are you sure you want to delete this task?",
			onConfirm: () => this.deleteTask(task.id, task.engagement_id)
		});
	};

	/* ---------- views ---------- */

	// The header's All / Assigned to me switch drives the same default views as the sidebar, through the sidebar when it is mounted
	selectDefaultView = viewId => {
		let view = TASK_DEFAULT_VIEWS.find(item => item.id === viewId) || TASK_DEFAULT_VIEWS[0];

		if (this.viewList && this.viewList.onDefaultViewSelected) {
			this.viewList.onDefaultViewSelected(view);
			return;
		}

		this.onDefaultViewSelected({ view, showInactive: this.state.shouldShowInactive });
	};

	onDefaultViewSelected = async ({ view, showInactive } = {}) => {
		let shouldShowInactive = !!showInactive;

		if (view && (view.id === "assignedToMe" || view.id === "assignedByMe")) {
			await this.update({ taskViewMode: view.id, shouldShowInactive, selectedTaskIds: [], page: 1, loading: true });
			await this.fetchTasks();
			await this.update({ loading: false });
			return;
		}

		// The views panel auto-selects "All" right after mounting, while the page's own first load is already running.
		// Skip the duplicate full reset in that window so the page loads with a single request.
		let isInitialAutoSelect = Date.now() - this.mountedAt < 2500 && this.state.taskViewMode === "all";

		if (isInitialAutoSelect) {
			if (shouldShowInactive !== this.state.shouldShowInactive) {
				await this.update({ shouldShowInactive });
				await this.fetchTasks();
			}
			return;
		}

		await this.update({ taskViewMode: "all" });
		await this.resetComponent({ shouldShowInactive });
	};

	onViewSelected = async ({ view, showInactive }) => {
		let { filters, sort, groupBy } = JSON.parse(view.schema);

		let { clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, assignedEngagements, dueDate } = filters;
		let { sortField, sortOrder } = sort || {};

		await this.update({
			engagementId,
			clientId,

			assignedUsers: assignedUsers || [],
			assignedStatuses: assignedStatuses || [],
			assignedClients: assignedClients || [],
			assignedClientGroups: assignedClientGroups || [],
			assignedEngagements: assignedEngagements || [],
			dueDate: dueDate || WORKFLOW.defaultDateFunction,

			sortField: sortField || ORDER_QUERIES.deadline,
			sortOrder: sortOrder || "asc",
			groupBy: groupBy || "none",

			taskViewMode: "all",
			shouldShowInactive: !!showInactive,
			selectedTaskIds: [],
			page: 1,
			isViewChanged: false
		});

		await this.fetchTasks();
	};

	getFilterSchema = () => {
		// TODO - Should add search to the view
		let {
			sortField,
			sortOrder,
			clientId,
			engagementId,
			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			assignedEngagements,
			dueDate,
			groupBy
		} = this.state;

		let schema = {
			filters: {
				clientId,
				engagementId,

				assignedUsers,
				assignedStatuses,
				assignedClients,
				assignedClientGroups,
				assignedEngagements,
				dueDate
			},
			columns: [], // Add functionality later
			sort: {
				sortField,
				sortOrder
			},
			groupBy
		};

		return schema;
	};

	/* ---------- derived data ---------- */

	getStatTiles() {
		let { facets, dueDate, assignedStatuses } = this.state;

		return this.memo("statTiles", [facets, dueDate, assignedStatuses], () => this.computeStatTiles());
	}

	computeStatTiles() {
		let { facets, dueDate, assignedStatuses } = this.state;

		let status = facets.status || {};
		let due = facets.due || {};
		let count = id => status[id] || 0;

		let open = count(STATUSES.pending.id) + count(STATUSES.ready.id) + count(STATUSES.in_progress.id);
		let overdue = due.overdue || 0;
		let today = due.today || 0;
		let thisWeek = due.thisWeek || 0;

		let dueIs = id => !!dueDate && dueDate.dateFunction === id;
		let statusIs = id => assignedStatuses.length === 1 && assignedStatuses[0] === id;

		let endOfWeek = moment()
			.endOf("week")
			.format("ddd, MMM D");

		return [
			{
				id: "overdue",
				label: "Overdue",
				value: overdue,
				meta: overdue ? "Need attention first" : "Nothing is late",
				tone: "red",
				active: dueIs("overdue"),
				onClick: () => this.setDuePreset(dueIs("overdue") ? "all" : "overdue")
			},
			{
				id: "today",
				label: "Due today",
				value: today,
				meta: today ? "Due before midnight" : "Nothing due today",
				tone: "amber",
				active: dueIs("today"),
				onClick: () => this.setDuePreset(dueIs("today") ? "all" : "today")
			},
			{
				id: "thisWeek",
				label: "Due this week",
				value: thisWeek,
				meta: `Through ${endOfWeek}`,
				tone: "blue",
				active: dueIs("thisWeek"),
				onClick: () => this.setDuePreset(dueIs("thisWeek") ? "all" : "thisWeek")
			},
			{
				id: "inProgress",
				label: "In progress",
				value: count(STATUSES.in_progress.id),
				meta: `of ${pluralize(open, "open task")}`,
				tone: "grey",
				active: statusIs(STATUSES.in_progress.id),
				onClick: () => this.setFilter({ assignedStatuses: statusIs(STATUSES.in_progress.id) ? [] : [STATUSES.in_progress.id] })
			},
			{
				id: "done",
				label: "Completed",
				value: count(STATUSES.done.id),
				meta: "Done in this scope",
				tone: "green",
				active: statusIs(STATUSES.done.id),
				onClick: () => this.setFilter({ assignedStatuses: statusIs(STATUSES.done.id) ? [] : [STATUSES.done.id] })
			}
		];
	}

	getEngagementOptions() {
		let { facets, assignedEngagements } = this.state;

		return this.memo("engagementOptions", [facets, assignedEngagements], () => this.computeEngagementOptions());
	}

	computeEngagementOptions() {
		let { facets, assignedEngagements } = this.state;

		let names = {};
		let counts = {};

		for (let engagement of facets.engagements || []) {
			names[engagement.id] = engagement.name;
			counts[engagement.id] = engagement.count;
		}

		for (let engagement of assignedEngagements) {
			if (!names[engagement.engagement_id]) {
				names[engagement.engagement_id] = engagement.engagementName;
			}
		}

		return Object.keys(names)
			.map(id => ({
				id: parseInt(id, 10),
				label: names[id],
				meta: counts[id] || 0,
				checked: assignedEngagements.some(engagement => String(engagement.engagement_id) === String(id))
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}

	getActiveChips() {
		let { search, assignedStatuses, assignedUsers, assignedClients, assignedClientGroups, assignedEngagements, dueDate, taskViewMode } = this.state;

		let chips = [];

		if (search && search.trim()) {
			chips.push({ key: "search", label: `Search: ${search.trim()}`, remove: () => this.setFilter({ search: "" }) });
		}

		for (let statusId of assignedStatuses) {
			let status = STATUSES[statusId];
			chips.push({ key: `status-${statusId}`, label: `Status: ${status ? status.name : statusId}`, remove: () => this.toggleStatus({ id: statusId }) });
		}

		if (taskViewMode !== "assignedToMe") {
			for (let user of assignedUsers) {
				chips.push({ key: `user-${user.user_id}`, label: `Assignee: ${user.userName}`, remove: () => this.toggleUser({ id: user.user_id, label: user.userName }) });
			}
		}

		for (let group of assignedClientGroups) {
			chips.push({
				key: `group-${group.client_group_id}`,
				label: `Client group: ${group.clientGroupName}`,
				remove: () => this.toggleClientGroup({ id: group.client_group_id, label: group.clientGroupName })
			});
		}

		for (let client of assignedClients) {
			chips.push({ key: `client-${client.client_id}`, label: `Client: ${client.clientName}`, remove: () => this.toggleClient({ id: client.client_id, label: client.clientName }) });
		}

		for (let engagement of assignedEngagements) {
			chips.push({
				key: `engagement-${engagement.engagement_id}`,
				label: `Engagement: ${engagement.engagementName}`,
				remove: () => this.toggleEngagement({ id: engagement.engagement_id, label: engagement.engagementName })
			});
		}

		if (dueDate && dueDate.dateFunction && dueDate.dateFunction !== "all") {
			let preset = DUE_PRESETS.find(item => item.id === dueDate.dateFunction);
			let dateFunction = WORKFLOW.dateFunctions[dueDate.dateFunction];

			chips.push({
				key: "due",
				label: `Due: ${preset ? preset.label : dateFunction ? dateFunction.display : dueDate.dateFunction}`,
				remove: () => this.setDuePreset("all")
			});
		}

		return chips;
	}

	getGroups(tasks) {
		let { groupBy } = this.state;

		return this.memo("groups", [tasks, groupBy], () => this.computeGroups(tasks, groupBy));
	}

	computeGroups(tasks, groupBy) {
		if (groupBy === "none") {
			return [{ key: "all", title: "", tone: null, tasks, showHeader: false }];
		}

		let buckets = [];

		let push = (key, title, tone, task) => {
			let bucket = buckets.find(item => item.key === key);

			if (!bucket) {
				bucket = { key, title, tone, tasks: [], showHeader: true };
				buckets.push(bucket);
			}

			bucket.tasks.push(task);
		};

		for (let task of tasks) {
			if (groupBy === "client") {
				push(`client-${task.client_id || 0}`, task.Client ? task.Client.name : "No client", "ready", task);
			} else if (groupBy === "status") {
				push(task.status, STATUSES[task.status] ? STATUSES[task.status].name : task.status, task.status, task);
			} else if (groupBy === "assignee") {
				let taskUser = task.task_users && task.task_users[0];
				push(`user-${taskUser ? taskUser.user_id : 0}`, taskUser && taskUser.User ? userFullName(taskUser.User) : "Unassigned", "ready", task);
			} else if (groupBy === "due") {
				let bucket = task.status === STATUSES.done.id ? "done" : dueBucket(task);
				push(bucket, DUE_GROUPS[bucket].label, DUE_GROUPS[bucket].tone, task);
			}
		}

		if (groupBy === "status") {
			buckets.sort((a, b) => STATUS_ORDER.indexOf(a.key) - STATUS_ORDER.indexOf(b.key));
		} else if (groupBy === "due") {
			buckets.sort((a, b) => DUE_GROUP_ORDER.indexOf(a.key) - DUE_GROUP_ORDER.indexOf(b.key));
		} else {
			buckets.sort((a, b) => a.title.localeCompare(b.title));
		}

		return buckets;
	}

	getClientGroupName(task) {
		let { clientGroupOptions } = this.state;

		if (!task.Client || !task.Client.client_group_id) {
			return "";
		}

		let group = clientGroupOptions.find(item => item.id === task.Client.client_group_id);

		return group ? group.name : "";
	}

	/* ---------- kanban ---------- */

	buildStatusKanbanStyle = () => {
		let { tasks } = this.state;

		let lanes = Object.values(STATUSES).map(status => {
			return {
				id: status.id,
				title: status.name,
				cards: [],
				style: {
					backgroundColor: "#eef0f5"
				}
			};
		});

		for (let task of tasks) {
			for (let lane of lanes) {
				if (task.status === lane.id) {
					lane.cards.push({
						id: task.id,
						task
					});
				}
			}
		}

		for (let lane of lanes) {
			lane.label = String(lane.cards.length);
		}

		return { lanes };
	};

	buildDeadlineKanbanStyle = () => {
		let { tasks } = this.state;

		let lanes = Object.values(DEADLINES).map(deadline => {
			return {
				id: deadline.id,
				title: deadline.name,
				range: deadline.range,
				cards: [],
				style: {
					backgroundColor: "#eef0f5"
				}
			};
		});

		// Object keeps track if a task has been categorized already
		let categorizedTasks = {};

		for (let task of tasks) {
			const deadlineDateOnly = toDateOnly({ value: task.deadline });
			if (!deadlineDateOnly) {
				continue;
			}

			const deadline = moment(deadlineDateOnly, "YYYY-MM-DD");

			for (let lane of lanes) {
				// Range is stored within the lane object
				let { start, end } = lane.range;

				// Confirm if the task is within the deadline range
				let isWithinDeadlineRange = deadline.isBetween(start, end, "hour", "[]");

				// Confirm that the task is within the range set for this particular lane and that we have not already categorized it before
				if (isWithinDeadlineRange && !categorizedTasks[task.id]) {
					// Cache a true value to indicate we have categorized this particular task
					categorizedTasks[task.id] = true;

					// Push the task on top the react-trello payload
					lane.cards.push({
						id: task.id,
						task
					});
				}
			}
		}

		for (let lane of lanes) {
			lane.label = String(lane.cards.length);
		}

		return { lanes };
	};

	LaneHeader = ({ title, label }) => {
		let tone = LANE_TONES[title] || "pending";

		return (
			<div className="task-lane__head">
				<span className={clsx("task-lane__dot", `task-lane__dot--${tone}`)} />
				<header className="task-lane__title">{title}</header>
				{typeof label !== "undefined" && <span className="task-lane__count">{label}</span>}
			</div>
		);
	};

	CustomCard = props => {
		let { id, task, onClick } = props;

		let due = dueMeta(task);

		return (
			<div
				key={id}
				className={clsx("task-card", `task-card--${task.status}`, { "task-card--dimmed": this.isDimmed(task) })}
				onClick={onClick}
				title={this.isDimmed(task) ? "Not the current step of this sequential engagement" : undefined}
			>
				<div className="task-card__top">
					<div className="task-card__label">{task.Client ? task.Client.name : "No client"}</div>
					<PermissionGate resource="task" action="delete">
						<button
							type="button"
							className="task-card__delete ft-task-card-delete"
							title="Delete task"
							aria-label="Delete task"
							onClick={event => {
								event.stopPropagation();
								this.askDeleteTask(task);
							}}
						>
							<Icon.Trash2 size={14} />
						</button>
					</PermissionGate>
				</div>

				<div className="task-card__title">{task.name}</div>
				{(task.description_preview || task.description) && <div className="task-card__description">{task.description_preview || task.description}</div>}

				<div className="task-card__meta">
					{task.Engagement && <span className="task-card__eng-name">{task.Engagement.name}</span>}
					{isSequential(task) && task.step > 0 && <span className="tasks-badge tasks-badge--step">Step {task.step}</span>}
					{task.sent_back_reason && (
						<span className="tasks-badge tasks-badge--sentback" title={task.sent_back_reason}>
							Sent back
						</span>
					)}
					{isInactiveEngagement(task) && <span className="tasks-badge tasks-badge--inactive">Inactive engagement</span>}
				</div>

				{task.CreatedByUser && <div className="task-card__assigned-by">Assigned by: {userFullName(task.CreatedByUser)}</div>}
        
				<div className="task-card__dates">
					<div className="task-card__dates__item">Created - {moment(task.created_at).format("YYYY-MM-DD")}</div>
					<div className={clsx("task-card__dates__item", `task-card__dates__item--${due.tone}`)}>Due - {this.formatDateOnly({ value: task.deadline })}</div>
				</div>

				<div className="task-card__footer">
					<PermissionGate resource="task" action="update">{this.renderStatusPill(task)}</PermissionGate>
					<div className="task-card__users ft-task-card-users">
						<UserBubbles engagementUsers={task.task_users} />
					</div>
				</div>
			</div>
		);
	};

	renderKanban() {
		let { kanbanStyle } = this.state;

		let data = {};

		if (kanbanStyle === KANBAN_STYLES.status) {
			data = this.buildStatusKanbanStyle();
		} else if (kanbanStyle === KANBAN_STYLES.deadline) {
			data = this.buildDeadlineKanbanStyle();
		}

		return (
			<div className="task-kanban-container">
				<div className="tasks__panel tasks-t__foot--board">{this.renderPager()}</div>
				<Board
					components={{
						Card: this.CustomCard,
						LaneHeader: this.LaneHeader
					}}
					cardDraggable={kanbanStyle === KANBAN_STYLES.status}
					className="task-kanban-container__board"
					style={{ backgroundColor: "transparent", overflowX: "auto", padding: 0 }}
					data={data}
					onCardClick={this.onCardClick}
					onCardMoveAcrossLanes={this.onCardMoveAcrossLanes}
				/>
			</div>
		);
	}

	/* ---------- render helpers ---------- */

	renderSortIcon = field => {
		let { sortField, sortOrder } = this.state;

		return (
			<Icon.ArrowUp
				size={13}
				className={clsx("tasks-t__sort-ic", {
					"tasks-t__sort-ic--asc": sortField === field && sortOrder === "asc",
					"tasks-t__sort-ic--desc": sortField === field && sortOrder === "desc"
				})}
			/>
		);
	};

	// Inline status change on a row or card
	renderStatusPill(task) {
		let { openMenu } = this.state;
		let menuName = `status:${task.id}`;
		let status = STATUSES[task.status] || { id: task.status, name: task.status };

		return (
			<FilterChip
				variant="pill"
				tone={task.status}
				label={status.name}
				labelClassName="ft-task-status-col"
				className="tasks-status-pill"
				title="Change status"
				open={openMenu === menuName}
				onOpen={() => this.openMenu(menuName)}
				onClose={this.closeMenus}
				single
				minWidth={190}
				options={STATUS_ORDER.map(id => ({ id, label: STATUSES[id].name, tone: id, checked: id === task.status }))}
				onToggle={option => {
					if (option.id !== task.status) {
						this.onTaskStatusUpdate({ taskId: task.id, status: option.id });
					} else {
						this.closeMenus();
					}
				}}
			/>
		);
	}

	renderFilterChips() {
		let { openMenu, assignedStatuses, assignedUsers, assignedClients, assignedClientGroups, assignedEngagements, dueDate, userOptions, clientOptions, clientGroupOptions, taskViewMode } = this.state;

		let counts = this.getCounts();
		let scopeTotal = this.getScopeTotal();

		let duePreset = DUE_PRESETS.find(item => item.id === dueDate.dateFunction);
		let dueFunction = WORKFLOW.dateFunctions[dueDate.dateFunction];
		let dueLabel = duePreset ? duePreset.chip : dueFunction ? `Due: ${dueFunction.display}` : "Due date";

		let clientCount = assignedClients.length + assignedClientGroups.length;

		return (
			<>
				<FilterChip
					className="ft-task-chip-status"
					label="Status"
					count={assignedStatuses.length}
					active={assignedStatuses.length > 0}
					open={openMenu === MENUS.status}
					onOpen={() => this.openMenu(MENUS.status)}
					onClose={this.closeMenus}
					title="Status"
					minWidth={260}
					options={STATUS_ORDER.map(id => ({
						id,
						label: STATUSES[id].name,
						tone: id,
						meta: counts.status[id] || 0,
						checked: assignedStatuses.includes(id)
					}))}
					onToggle={this.toggleStatus}
					onSelectAll={this.selectAllStatuses}
					onClear={this.clearStatuses}
				/>

				{taskViewMode !== "assignedToMe" && (
					<FilterChip
						className="ft-task-chip-users"
						label="Assignee"
						count={assignedUsers.length}
						active={assignedUsers.length > 0}
						open={openMenu === MENUS.users}
						onOpen={() => this.openMenu(MENUS.users)}
						onClose={this.closeMenus}
						searchable
						searchPlaceholder="Search team members…"
						minWidth={320}
						options={userOptions.map(user => ({
							id: user.id,
							label: user.name,
							avatar: initialsOf(user.name),
							meta: counts.user[user.id] || 0,
							checked: assignedUsers.some(assigned => assigned.user_id === user.id)
						}))}
						onToggle={this.toggleUser}
						onClear={this.clearUsers}
					/>
				)}

				<FilterChip
					className="ft-task-chip-clients"
					label="Client"
					count={clientCount}
					active={clientCount > 0}
					open={openMenu === MENUS.clients}
					onOpen={() => this.openMenu(MENUS.clients)}
					onClose={this.closeMenus}
					searchable
					searchPlaceholder="Search clients and groups…"
					minWidth={360}
					sections={[
						{
							title: "Client groups",
							options: clientGroupOptions.map(group => ({
								id: group.id,
								label: group.name,
								meta: counts.group[group.id] || 0,
								checked: assignedClientGroups.some(assigned => assigned.client_group_id === group.id),
								isGroup: true
							}))
						},
						{
							title: "Clients",
							options: clientOptions.map(client => ({
								id: client.id,
								label: client.name,
								type: client.type,
								meta: counts.client[client.id] || 0,
								checked: assignedClients.some(assigned => assigned.client_id === client.id)
							}))
						}
					]}
					onToggle={option => (option.isGroup ? this.toggleClientGroup(option) : this.toggleClient(option))}
					onClear={this.clearClients}
				/>

				<FilterChip
					className="ft-task-chip-engagements"
					label="Engagement"
					count={assignedEngagements.length}
					active={assignedEngagements.length > 0}
					open={openMenu === MENUS.engagements}
					onOpen={() => this.openMenu(MENUS.engagements)}
					onClose={this.closeMenus}
					searchable
					searchPlaceholder="Search engagements…"
					minWidth={360}
					options={this.getEngagementOptions()}
					onToggle={this.toggleEngagement}
					onClear={this.clearEngagements}
				/>

				<FilterChip
					className="ft-task-chip-due"
					icon={<Icon.Calendar size={14} />}
					label={dueLabel}
					active={dueDate.dateFunction !== "all"}
					open={openMenu === MENUS.due}
					onOpen={() => this.openMenu(MENUS.due)}
					onClose={this.closeMenus}
					single
					title="Due date"
					minWidth={280}
					options={DUE_PRESETS.map(preset => ({
						id: preset.id,
						label: preset.label,
						meta: preset.id === "all" ? scopeTotal : counts.due[preset.id] || 0,
						checked: dueDate.dateFunction === preset.id
					}))}
					onToggle={option => this.setDuePreset(option.id)}
					extra={
						<button type="button" className="filter-chip__extra ft-task-chip-custom-range" onClick={this.onShowFilterModal}>
							<Icon.Calendar size={14} />
							<span>Custom range…</span>
						</button>
					}
				/>
			</>
		);
	}

	presetMatches(presetId, task) {
		let bucket = dueBucket(task);

		if (presetId === "all") {
			return true;
		}

		if (presetId === "thisWeek") {
			return bucket === "today" || bucket === "thisWeek";
		}

		if (presetId === "noDueDate") {
			return bucket === "none";
		}

		return bucket === presetId;
	}

	renderToolbar() {
		let { search, showFilters, pageView, openMenu, groupBy, sortField, sortOrder, density, fetching } = this.state;

		let activeChips = this.getActiveChips();
		let sortOption = SORT_OPTIONS.find(option => option.id === sortField);
		let groupOption = GROUP_BY_OPTIONS.find(option => option.id === groupBy);

		return (
			<div className={clsx("tasks__panel", "tasks__toolbar", { "tasks__toolbar--busy": fetching })}>
				<div className="tasks__toolbar__row">
					<PermissionGate resource="task" action="search">
						<SearchInput onChange={this.onSearch} value={search} placeholder="Search tasks..." />
					</PermissionGate>

					{showFilters && <div className="tasks__toolbar__sep" />}
					{showFilters && this.renderFilterChips()}

					<div className="tasks__toolbar__right">
					{pageView === "list" && (
						<FilterChip
							variant="button"
							className="ft-task-group-btn"
							icon={<Icon.Grid size={14} />}
							label={`Group: ${groupOption ? groupOption.label : "None"}`}
							open={openMenu === MENUS.group}
							onOpen={() => this.openMenu(MENUS.group)}
							onClose={this.closeMenus}
							single
							align="right"
							title="Group rows by"
							minWidth={200}
							options={GROUP_BY_OPTIONS.map(option => ({ id: option.id, label: option.label, checked: option.id === groupBy }))}
							onToggle={this.setGroupBy}
						/>
					)}

					<FilterChip
						variant="button"
						className="ft-task-sort-btn"
						icon={<Icon.Sliders size={14} />}
						label={`Sort: ${sortOption ? sortOption.label : "Deadline"}`}
						open={openMenu === MENUS.sort}
						onOpen={() => this.openMenu(MENUS.sort)}
						onClose={this.closeMenus}
						single
						align="right"
						title="Sort by"
						minWidth={220}
						options={SORT_OPTIONS.map(option => ({ id: option.id, label: option.label, checked: option.id === sortField }))}
						onToggle={this.setSortField}
						extra={
							<button type="button" className="filter-chip__extra ft-task-sort-dir" onClick={this.flipSortOrder}>
								<Icon.Repeat size={14} />
								<span>{sortOrder === "asc" ? "Ascending · switch to descending" : "Descending · switch to ascending"}</span>
							</button>
						}
					/>

					{pageView === "list" && (
						<button
							type="button"
							className={clsx("tasks__icon-btn", "ft-task-density", { "tasks__icon-btn--on": density === DENSITIES.compact })}
							onClick={this.toggleDensity}
							title={density === DENSITIES.compact ? "Comfortable rows" : "Compact rows"}
							aria-label="Toggle row density"
						>
							<Icon.AlignJustify size={16} />
						</button>
					)}
					</div>
				</div>

				{activeChips.length > 0 && (
					<div className="tasks__active">
						<span className="tasks__active__label">Filters</span>
						{activeChips.map(chip => (
							<span className="tasks__tag" key={chip.key}>
								{chip.label}
								<button type="button" className="tasks__tag__x" aria-label="Remove filter" onClick={chip.remove}>
									<Icon.X size={11} strokeWidth={3} />
								</button>
							</span>
						))}
						<button type="button" className="tasks__link-btn ft-task-clear-filters" onClick={this.clearAllFilters}>
							Clear all
						</button>
					</div>
				)}
			</div>
		);
	}

	renderRow(task) {
		let { selectedTaskIds, openMenu } = this.state;

		return (
			<TaskRow
				key={task.id}
				task={task}
				selected={selectedTaskIds.includes(task.id)}
				dimmed={this.isDimmed(task)}
				statusMenuOpen={openMenu === `status:${task.id}`}
				clientGroupName={this.getClientGroupName(task)}
				handlers={this.rowHandlers}
			/>
		);
	}

	renderList() {
		let { tasks, selectedTaskIds, density } = this.state;

		let groups = this.getGroups(tasks);
		let isAllSelected = tasks.length > 0 && tasks.every(task => selectedTaskIds.includes(task.id));

		return (
			<div className="tasks__panel tasks__list">
				<ScrollableContainer>
					<div className={clsx("t", "t--compact", "tasks-t", { "tasks-t--compact": density === DENSITIES.compact })}>
						<div className="t__header tasks-t__header">
							<div className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--check")}>
								<Checkbox name="selectAll" checked={isAllSelected} onChange={this.onSelectAll} isSmall />
							</div>

							<div onClick={() => this.sortBy(ORDER_QUERIES.name)} className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--task", "tasks-t__th--sortable", "ft-task-title")}>
								Task {this.renderSortIcon(ORDER_QUERIES.name)}
							</div>

							<div
								onClick={() => this.sortBy(ORDER_QUERIES.organization)}
								className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--client", "tasks-t__th--sortable", "tasks__header__item--client-name")}
							>
								Client {this.renderSortIcon(ORDER_QUERIES.organization)}
							</div>

							<div className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--users")}>Assignees</div>

							<div onClick={() => this.sortBy(ORDER_QUERIES.status)} className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--status", "tasks-t__th--sortable", "ft-task-status")}>
								Status {this.renderSortIcon(ORDER_QUERIES.status)}
							</div>

							<div onClick={() => this.sortBy(ORDER_QUERIES.deadline)} className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--due", "tasks-t__th--sortable", "ft-task-deadline")}>
								Due {this.renderSortIcon(ORDER_QUERIES.deadline)}
							</div>

							<div className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--by")}>Assigned by</div>

							<div className={clsx("t__header__item", "tasks-t__col", "tasks-t__col--actions")} />
						</div>

						{groups.map(group => (
							<React.Fragment key={group.key}>
								{group.showHeader && (
									<div className="tasks-t__group">
										{group.tone && <span className={clsx("tasks-t__group__dot", `tasks-t__group__dot--${group.tone}`)} />}
										{group.title}
										<span className="tasks-t__group__count">{group.tasks.length}</span>
									</div>
								)}
								{group.tasks.map(task => this.renderRow(task))}
							</React.Fragment>
						))}
					</div>
				</ScrollableContainer>

				{this.renderPager()}
			</div>
		);
	}

	// Range label, rows-per-page menu and page buttons, shared by the list and the board
	renderPager() {
		let { tasks, total, page, pageSize, openMenu } = this.state;

		let pageCount = this.getPageCount();
		let start = total === 0 ? 0 : (page - 1) * pageSize + 1;
		let end = Math.min(total, (page - 1) * pageSize + tasks.length);

		return (
			<div className="tasks-t__foot">
				<span>
					Showing <strong>{total === 0 ? "0 tasks" : `${start.toLocaleString()}–${end.toLocaleString()} of ${pluralize(total, "task").replace(/^\d+/, total.toLocaleString())}`}</strong>
				</span>

				<div className="tasks-t__pager">
					<FilterChip
						variant="button"
						className="ft-task-page-size"
						label={`${pageSize} per page`}
						open={openMenu === MENUS.pageSize}
						onOpen={() => this.openMenu(MENUS.pageSize)}
						onClose={this.closeMenus}
						single
						align="right"
						title="Rows per page"
						minWidth={170}
						options={PAGE_SIZES.map(size => ({ id: size, label: `${size} per page`, checked: size === pageSize }))}
						onToggle={this.setPageSize}
					/>
					<button type="button" className="tasks__icon-btn ft-task-page-prev" aria-label="Previous page" title="Previous page" disabled={page <= 1} onClick={() => this.setPage(page - 1)}>
						<Icon.ChevronLeft size={15} />
					</button>
					<span className="tasks-t__pager__n">
						Page {page} of {pageCount}
					</span>
					<button type="button" className="tasks__icon-btn ft-task-page-next" aria-label="Next page" title="Next page" disabled={page >= pageCount} onClick={() => this.setPage(page + 1)}>
						<Icon.ChevronRight size={15} />
					</button>
				</div>
			</div>
		);
	}

	renderBulkBar() {
		let { selectedTaskIds } = this.state;
		let count = selectedTaskIds.length;

		if (count === 0) {
			return null;
		}

		return (
			<div className="tasks__bulk" role="region" aria-label="Bulk actions">
				<span className="tasks__bulk__n">{pluralize(count, "task")} selected</span>

				<PermissionGate resource="task" action="update">
					<button type="button" className="tasks__bulk__btn ft-task-bulk-done" onClick={this.bulkMarkDone}>
						<Icon.Check size={14} />
						Mark done
					</button>
				</PermissionGate>
				<PermissionGate resource="task" action="update">
					<button type="button" className="tasks__bulk__btn ft-task-bulk-reassign" onClick={() => this.openBulkAction(BULK_ACTIONS.reassign)}>
						<Icon.User size={14} />
						Reassign
					</button>
				</PermissionGate>
				<PermissionGate resource="task" action="update">
					<button type="button" className="tasks__bulk__btn ft-task-bulk-due" onClick={() => this.openBulkAction(BULK_ACTIONS.due)}>
						<Icon.Calendar size={14} />
						Change due date
					</button>
				</PermissionGate>
				<PermissionGate resource="task" action="delete">
					<button type="button" className="tasks__bulk__btn tasks__bulk__btn--danger ft-task-bulk-delete" onClick={this.bulkDelete}>
						<Icon.Trash2 size={14} />
						Delete Selected {count}
					</button>
				</PermissionGate>
				<button type="button" className="tasks__bulk__btn tasks__bulk__btn--icon" aria-label="Clear selection" title="Clear selection" onClick={this.clearSelection}>
					<Icon.X size={14} />
				</button>
			</div>
		);
	}

	render() {
		let {
			loading,
			taskId,

			// Filters
			clientId,
			engagementId,
			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			dueDate,
			kanbanStyle,
			pageView,
			taskViewMode,

			showTaskModal,
			showFilterModal,
			showViews,
			showFilters,

			title,

			tasks,
			total,
			isViewChanged,
			bulkAction,
			selectedTaskIds,
			confirm,
			isNotAllowedResource
		} = this.state;

		if (isNotAllowedResource) {
			return <RestrictedAccess />;
		}

		let hiddenCount = Math.max(0, this.getScopeTotal() - total);
		let scopeLabel = taskViewMode === "assignedToMe" ? "Assigned to you by someone else" : "All tasks";
		let summary = `${scopeLabel} · ${total.toLocaleString()} ${total === 1 ? "task" : "tasks"} match${hiddenCount > 0 ? ` · ${hiddenCount.toLocaleString()} hidden by filters` : ""}`;

		return (
			<div className="tasks">
				<div className="tasks__header">
					<div className="tasks__heading">
						<div className="tasks__crumb">
							Workflow <span>/</span> {title}
						</div>
						<h1 className="tasks__h1">
							{title} <span className="tasks__count">{total.toLocaleString()}</span>
						</h1>
						<p className="tasks__sub">{summary}</p>
					</div>

					<div className="tasks__header__actions">
						{showViews && (
							<div className="tasks-seg" role="group" aria-label="Default view">
								<button
									type="button"
									className={clsx("tasks-seg__b", "ft-task-view-all", { "tasks-seg__b--on": taskViewMode !== "assignedToMe" })}
									onClick={() => this.selectDefaultView("all")}
								>
									All
								</button>
								<button
									type="button"
									className={clsx("tasks-seg__b", "ft-task-view-mine", { "tasks-seg__b--on": taskViewMode === "assignedToMe" })}
									onClick={() => this.selectDefaultView("assignedToMe")}
								>
									Assigned to me
								</button>
							</div>
						)}

						<PermissionGate resource="task" action="toggleViews">
							<div className="tasks-seg" role="group" aria-label="Layout">
								<button
									type="button"
									data-tab-id="list"
									className={clsx("tasks-seg__b", "tasks-seg__b--icon", { "tasks-seg__b--on": pageView === "list" })}
									onClick={() => this.onPageViewSelect({ id: "list" })}
									title="List"
									aria-label="List view"
								>
									<ListChecks size={18} strokeWidth={1.75} />
								</button>
								<button
									type="button"
									data-tab-id="kanban"
									className={clsx("tasks-seg__b", "tasks-seg__b--icon", { "tasks-seg__b--on": pageView === "kanban" })}
									onClick={() => this.onPageViewSelect({ id: "kanban" })}
									title="Board"
									aria-label="Board view"
								>
									<Trello size={18} strokeWidth={1.75} />
								</button>
							</div>
						</PermissionGate>

						{pageView === "kanban" && (
							<PermissionGate resource="task" action="deadlineStatusView">
								<div className="tasks-seg" role="group" aria-label="Board lanes">
									<button
										type="button"
										data-tab-id="status"
										className={clsx("tasks-seg__b", { "tasks-seg__b--on": kanbanStyle === KANBAN_STYLES.status })}
										onClick={() => this.onKanbanStyleSelect({ id: KANBAN_STYLES.status })}
									>
										By status
									</button>
									<button
										type="button"
										data-tab-id="deadline"
										className={clsx("tasks-seg__b", { "tasks-seg__b--on": kanbanStyle === KANBAN_STYLES.deadline })}
										onClick={() => this.onKanbanStyleSelect({ id: KANBAN_STYLES.deadline })}
									>
										By deadline
									</button>
								</div>
							</PermissionGate>
						)}

						<PermissionGate resource="task" action="showFilters">
							{showFilters && (
								<button
									type="button"
									className={clsx("tasks__btn", "tasks__btn--icon", "tasks__header__icon-btn", "ft-tasks-filter-btn")}
									onClick={this.onShowFilterModal}
									title="All filters"
									aria-label="All filters"
								>
									<Icon.Sliders size={16} />
								</button>
							)}
						</PermissionGate>

						<PermissionGate resource="task" action="create">
							<button type="button" className={clsx("tasks__btn", "tasks__btn--primary", "ft-task-add")} onClick={this.onNewTask}>
								<Icon.Plus size={15} strokeWidth={2.4} />
								New task
							</button>
						</PermissionGate>
					</div>
				</div>

				<TaskStats tiles={this.getStatTiles()} />

				<div className="tasks__layout">
					<PermissionGate resource="task" action="showViewsList">
						{showViews && (
							<ViewList
								ref={element => (this.viewList = element)}
								type={"task"}
								isViewChanged={isViewChanged}
								getSchema={this.getFilterSchema}
								onViewSelected={this.onViewSelected}
								onDefaultViewSelected={this.onDefaultViewSelected}
							/>
						)}
					</PermissionGate>

					<div className="tasks__main">
						{this.renderToolbar()}

						{loading && (
							<div className="container-loader">
								<Loader />
							</div>
						)}

						{tasks.length === 0 && !loading && (
							<div className="container-empty tasks__empty">
								<div>
									<Icon.Frown size={64} />
								</div>
								<div>... no tasks ...</div>
								{this.getActiveChips().length > 0 && (
									<button type="button" className="tasks__btn tasks__btn--sm tasks__empty__btn" onClick={this.clearAllFilters}>
										Clear all filters
									</button>
								)}
							</div>
						)}

						{tasks.length > 0 && !loading && pageView === "kanban" && this.renderKanban()}
						{tasks.length > 0 && !loading && pageView === "list" && this.renderList()}
					</div>
				</div>

				{this.renderBulkBar()}

				<TaskModal show={showTaskModal} clientId={clientId} taskId={taskId} engagementId={engagementId} onClose={this.onTaskClose} {...this.props} />
				<TaskFilterModal
					show={showFilterModal}
					onClose={this.onTaskFilterModalClose}
					onFilterChange={this.onFilterChange}
					filters={{ clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, dueDate }}
				/>

				<BulkActionModal show={!!bulkAction} action={bulkAction} count={selectedTaskIds.length} onClose={this.onBulkActionClose} />

				<ConfirmationModal title={confirm ? confirm.title : ""} warning={confirm ? confirm.warning : ""} show={!!confirm} onClose={this.onConfirmClose} />
			</div>
		);
	}

	onPageViewSelect = async pageView => {
		await this.update({ pageView: pageView.id, openMenu: null, selectedTaskIds: [] });
	};

	onKanbanStyleSelect = async kanbanStyle => {
		await this.update({ kanbanStyle: kanbanStyle.id, openMenu: null });
	};
}

export default Tasks;
